import { useMemo, useState } from "react";
import { relievingPressureKpaAbs, type ValveType } from "../calc/gas";
import { sizeSteam, type SteamSizingInput } from "../calc/steam";
import { selectOrificeWithParallel } from "../calc/orifice";
import { Field, Item, VALVE_LABELS, num } from "./formBits";

interface FormState {
  flow: string;
  setPressure: string;
  overpressure: string;
  saturated: boolean;
  temperature: string;
  valveType: ValveType;
  kb: string;
  kc: string;
}

/** 초기값은 API 520 Part I 5.7.2 Example 4 조건이다. 결과는 L 오리피스. */
const INITIAL: FormState = {
  flow: "69615",
  // 1600 psig = 11,032 kPag → ×1.1 + 101.325 = 12,236 kPa abs
  setPressure: "11032",
  overpressure: "10",
  saturated: false,
  temperature: "433.9",
  valveType: "conventional",
  kb: "",
  kc: "1.0",
};

export function SteamSizingPanel() {
  const [f, setF] = useState<FormState>(INITIAL);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setF((prev) => ({ ...prev, [key]: value }));

  const p1 = useMemo(
    () => relievingPressureKpaAbs(num(f.setPressure), num(f.overpressure)),
    [f.setPressure, f.overpressure],
  );

  const sizing = useMemo(() => {
    const kb = f.kb.trim() === "" ? undefined : num(f.kb);
    const input: SteamSizingInput = {
      requiredFlowKgH: num(f.flow),
      relievingPressureKpaAbs: p1,
      saturated: f.saturated,
      valveType: f.valveType,
      combinationCorrection: num(f.kc),
      ...(f.saturated ? {} : { relievingTemperatureC: num(f.temperature) }),
      ...(kb === undefined ? {} : { bellowsBackpressureCorrection: kb }),
    };
    return sizeSteam(input);
  }, [f, p1]);

  const selection = useMemo(
    () => (sizing.ok ? selectOrificeWithParallel(sizing.requiredAreaMm2) : null),
    [sizing],
  );

  return (
    <section className="card">
      <h2>수증기 사이징</h2>
      <p className="hint">
        API 520 Part I 10판 5.7절, Napier 식. 과열보정 KSH 는 Table 13 에서
        압력·온도 양방향 보간으로 구합니다. 초기값은 표준서 5.7.2 예제 조건입니다.
      </p>

      <div className="grid-form">
        <Field label="소요 유량 W" unit="kg/h" help="flowMass">
          <input value={f.flow} onChange={(e) => set("flow")(e.target.value)} />
        </Field>
        <Field label="설정압" unit="kPag" help="setPressure">
          <input
            value={f.setPressure}
            onChange={(e) => set("setPressure")(e.target.value)}
          />
        </Field>
        <Field label="허용 과압" unit="%" help="overpressure">
          <input
            value={f.overpressure}
            onChange={(e) => set("overpressure")(e.target.value)}
          />
        </Field>
        <Field label="증기 상태" unit="" help="steamState">
          <select
            value={f.saturated ? "sat" : "sup"}
            onChange={(e) => set("saturated")(e.target.value === "sat")}
          >
            <option value="sup">과열증기 (Superheated)</option>
            <option value="sat">포화증기 (Saturated)</option>
          </select>
        </Field>
        <Field
          label="배출 온도"
          unit={f.saturated ? "포화는 불필요" : "℃"}
          help="steamTemperatureC"
        >
          <input
            value={f.temperature}
            disabled={f.saturated}
            onChange={(e) => set("temperature")(e.target.value)}
          />
        </Field>
        <Field label="밸브 형식" unit="" help="valveType">
          <select
            value={f.valveType}
            onChange={(e) => set("valveType")(e.target.value as ValveType)}
          >
            {(Object.keys(VALVE_LABELS) as ValveType[]).map((t) => (
              <option key={t} value={t}>
                {VALVE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="배압 보정 Kb"
          unit={f.valveType === "balanced-bellows" ? "제작사 자료" : "해당 없음"}
          help="kbBellows"
        >
          <input
            value={f.kb}
            placeholder={
              f.valveType === "balanced-bellows" ? "예: 0.9" : "1.0 고정"
            }
            disabled={f.valveType !== "balanced-bellows"}
            onChange={(e) => set("kb")(e.target.value)}
          />
        </Field>
        <Field label="파열판 조합 Kc" unit="없으면 1.0" help="kc">
          <input value={f.kc} onChange={(e) => set("kc")(e.target.value)} />
        </Field>
      </div>

      {!sizing.ok ? (
        <div className="result result--error" role="alert">
          {sizing.message}
        </div>
      ) : (
        <div className="result result--ok">
          <div className="result__headline">
            <span className="result__label">소요 면적</span>
            <strong className="result__value">
              {sizing.requiredAreaMm2.toLocaleString("ko-KR", {
                maximumFractionDigits: 0,
              })}
            </strong>
            <span className="result__label">mm²</span>
          </div>

          {selection?.ok && (
            <div className="result__headline" style={{ marginTop: 10 }}>
              <span className="result__label">선정 오리피스</span>
              <strong className="result__value">
                {selection.orifice.designation}
              </strong>
              {selection.valveCount > 1 && (
                <span className="badge">{selection.valveCount}개 병렬</span>
              )}
              <span className="muted">
                여유율 {selection.marginPct.toFixed(1)} %
              </span>
            </div>
          )}
          {selection && !selection.ok && (
            <p className="muted" style={{ marginTop: 10 }}>
              {selection.message}
            </p>
          )}

          <h3>중간값 (계산서 기재용)</h3>
          <dl className="result__grid">
            <Item label="적용 식" value={sizing.equation} />
            <Item
              label="배출 압력 P1"
              value={`${p1.toLocaleString("ko-KR", { maximumFractionDigits: 1 })} kPa abs`}
            />
            <Item
              label={`KN (${sizing.intermediates.knEquation.replace("API 520 Part I ", "식 ")})`}
              value={sizing.intermediates.kn.toFixed(4)}
            />
            <Item
              label={
                sizing.intermediates.kshSource === "saturated"
                  ? "KSH (포화증기)"
                  : sizing.intermediates.kshSource === "override"
                    ? "KSH (직접 지정)"
                    : `KSH (Table 13${sizing.intermediates.kshInterpolated ? ", 보간" : ""})`
              }
              value={sizing.intermediates.ksh.toFixed(4)}
            />
            <Item label="Kd" value={sizing.intermediates.kd.toFixed(3)} />
            <Item label="Kb" value={sizing.intermediates.kb.toFixed(3)} />
            <Item label="Kc" value={sizing.intermediates.kc.toFixed(3)} />
          </dl>

          {sizing.warnings.length > 0 && (
            <ul className="warnings">
              {sizing.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
